import React, { useState, useEffect, useRef } from 'react';
import { AlertCircle, User, Users, Settings, PlayCircle, ChevronLeft, Link, Share2, Clock } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { v4 as uuidv4 } from 'uuid';
import { database } from './firebase';
import { ref, onValue, set, update, off } from 'firebase/database';
import validPathsData from './paths.json';
import { Helmet } from 'react-helmet';
import { Analytics } from '@vercel/analytics/react';

// Utility functions
const generateBoard = () => {
  const rowSizes = [4, 5, 6, 7, 6, 5, 4];
  const pillars = [];
  let pillarId = 0;
  
  // Generate pillar coordinates with proper offset for axial coordinates
  for (let row = 0; row < rowSizes.length; row++) {
    const size = rowSizes[row];
    const rowOffset = Math.floor((7 - size) / 2);
    
    for (let col = 0; col < size; col++) {
      // Calculate x,y positions for hexagonal layout
      const xOffset = (7 - size) / 2; // Center each row
      const x = (col + xOffset) * 60;
      const y = row * 52;
      
      // Store both display coordinates and logical coordinates
      pillars.push({
        id: pillarId++,
        row,
        col,
        x,
        y,
        // Store axial coordinates for proper path detection
        q: col - rowOffset,
        r: row,
        connections: []
      });
    }
  }

  return pillars;
};


// Find potential triangles based on three pillar IDs
const findTriangle = (p1, p2, p3, edges) => {
  const containsEdge = (a, b) => {
    return edges.some(edge => 
      (edge.start === a && edge.end === b) || 
      (edge.start === b && edge.end === a)
    );
  };
  
  return containsEdge(p1, p2) && containsEdge(p2, p3) && containsEdge(p1, p3) ? [p1, p2, p3] : null;
};

// Load valid paths from JSON
const loadValidPathsFromJson = () => {
  const paths = [];
  Object.entries(validPathsData).forEach(([startPillar, pathData]) => {
    Object.values(pathData).forEach(path => {
      // Check if this path already exists in reverse order
      const pathExists = paths.some(existingPath => 
        existingPath.join(',') === path.slice().reverse().join(',')
      );
      
      if (!pathExists) {
        paths.push(path);
      }
    });
  });
  return paths;
};

// AI player logic
const makeAIMove = (pillars, edges, validPaths, triangles) => {
  // Get all possible moves (starting and ending pillar pairs)
  const possibleMoves = [];
  
  validPaths.forEach(path => {
    const startPillarId = path[0];
    const endPillarId = path[3];
    
    // Create edges for this path
    const newEdges = [
      { start: path[0], end: path[1] },
      { start: path[1], end: path[2] },
      { start: path[2], end: path[3] }
    ];
    
    // Check if at least one edge is new
    const atLeastOneNewEdge = newEdges.some(newEdge => 
      !edges.some(edge => 
        (edge.start === newEdge.start && edge.end === newEdge.end) || 
        (edge.start === newEdge.end && edge.end === newEdge.start)
      )
    );
    
    if (atLeastOneNewEdge) {
      // Count how many triangles this move would create
      const tempEdges = [...edges];
      
      // Add new edges
      newEdges.forEach(newEdge => {
        if (!tempEdges.some(edge => 
            (edge.start === newEdge.start && edge.end === newEdge.end) || 
            (edge.start === newEdge.end && edge.end === newEdge.start)
          )) {
          tempEdges.push(newEdge);
        }
      });
      
      const existingTriangleCount = triangles.length;
      const allTriangles = [];
      
      // Check all possible triangles
      for (let i = 0; i < pillars.length; i++) {
        for (let j = i + 1; j < pillars.length; j++) {
          for (let k = j + 1; k < pillars.length; k++) {
            const pi = pillars[i].id;
            const pj = pillars[j].id;
            const pk = pillars[k].id;
            
            const triangle = findTriangle(pi, pj, pk, tempEdges);
            
            if (triangle) {
              allTriangles.push([pi, pj, pk]);
            }
          }
        }
      }
      
      const newTrianglesCount = allTriangles.length - existingTriangleCount;
      
      possibleMoves.push({
        startPillarId,
        endPillarId,
        newTrianglesCount,
        path
      });
    }
  });
  
  // No valid moves
  if (possibleMoves.length === 0) {
    return null;
  }
  
  // Sort moves by number of triangles created (descending)
  possibleMoves.sort((a, b) => b.newTrianglesCount - a.newTrianglesCount);
  
  // Choose the best move
  return possibleMoves[0];
};

// Main Component
const ChainTriangleGame = () => {
  // Game states
  const [gameState, setGameState] = useState('start'); // 'start', 'options', 'game', 'createMultiplayer', 'joinMultiplayer'
  const [gameMode, setGameMode] = useState('twoPlayer'); // 'onePlayer', 'twoPlayer', 'onlineMultiplayer'
  const [playerColors, setPlayerColors] = useState({
    1: '#3b82f6', // blue-500
    2: '#ef4444'  // red-500
  });
  const [playerNames, setPlayerNames] = useState({
    1: 'Player 1',
    2: 'Player 2'
  });
  
  // Game board states
  const [pillars, setPillars] = useState([]);
  const [edges, setEdges] = useState([]);
  const [triangles, setTriangles] = useState([]);
  const [selections, setSelections] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(1);
  const [scores, setScores] = useState({ 1: 0, 2: 0 });
  const [remainingMoves, setRemainingMoves] = useState({ 1: 20, 2: 20 });
  const [gameOver, setGameOver] = useState(false);
  const [message, setMessage] = useState("");
  const [highlightedPillars, setHighlightedPillars] = useState([]);
  const [validPaths, setValidPaths] = useState([]);
  const [aiThinking, setAiThinking] = useState(false);
  
  // New multiplayer states
  const [gameId, setGameId] = useState('');
  const [playerRole, setPlayerRole] = useState(1); // 1 = host, 2 = guest
  const [isConnected, setIsConnected] = useState(false);
  const [waitingForPlayer, setWaitingForPlayer] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [opponentConnected, setOpponentConnected] = useState(false);
  
  // Refs for Firebase listeners
  const gameRef = useRef(null);
  const connectionRef = useRef(null);

  // New states for timer and confirmation
  const [moveTimer, setMoveTimer] = useState(60); // 60 seconds per move
  const [timerActive, setTimerActive] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState('');
  const timerRef = useRef(null);

  // Add these new states for game challenges
  const [showChallenge, setShowChallenge] = useState(false);
  const [challengeFrom, setChallengeFrom] = useState(null);

  // Initialize the game board
  useEffect(() => {
    if (gameState === 'game') {
      const newPillars = generateBoard();
      setPillars(newPillars);
      setMessage(`${playerNames[1]}'s turn`);
      // Start the timer for the first player
      resetAndStartTimer();
    }
  }, [gameState, playerNames]);

  useEffect(() => {
    if (pillars.length === 0) return;
    setValidPaths(loadValidPathsFromJson());
  }, [pillars]);

  // Create a new multiplayer game
  const createMultiplayerGame = () => {
    const newGameId = uuidv4().substring(0, 8);
    setGameId(newGameId);
    setPlayerRole(1); // Host is player 1
    
    const url = `${window.location.origin}?game=${newGameId}`;
    setShareUrl(url);
    
    // Initialize game in Firebase
    const gameData = {
      created: Date.now(),
      host: {
        name: playerNames[1],
        color: playerColors[1]
      },
      status: 'waiting',
      currentPlayer: 1,
      edges: [],
      triangles: [],
      scores: { 1: 0, 2: 0 },
      remainingMoves: { 1: 20, 2: 20 }
    };
    
    set(ref(database, `games/${newGameId}`), gameData)
      .then(() => {
        console.log("Game created successfully");
        
        // Listen for opponent joining
        gameRef.current = ref(database, `games/${newGameId}`);
        onValue(gameRef.current, (snapshot) => {
          const data = snapshot.val();
          if (data && data.guest && data.status === 'active') {
            console.log("Opponent joined:", data.guest);
            setOpponentConnected(true);
            setPlayerNames({
              1: data.host.name,
              2: data.guest.name
            });
            setPlayerColors({
              1: data.host.color,
              2: data.guest.color
            });
            setWaitingForPlayer(false);
            setIsConnected(true);
            setupGameStateListener(newGameId);
          }
        });
        
        setWaitingForPlayer(true);
        setGameState('createMultiplayer');
      })
      .catch(error => {
        console.error("Error creating game:", error);
        setMessage("Error creating game. Please try again.");
      });
  };

  // Join an existing multiplayer game
  const joinMultiplayerGame = (gameIdToJoin) => {
    if (!gameIdToJoin) {
      // Extract from URL if not provided
      const urlParams = new URLSearchParams(window.location.search);
      gameIdToJoin = urlParams.get('game');
      
      if (!gameIdToJoin) {
        setMessage("Invalid game link");
        return;
      }
    }
    
    setGameId(gameIdToJoin);
    setPlayerRole(2); // Guest is player 2
    
    // Check if game exists
    const gameReference = ref(database, `games/${gameIdToJoin}`);
    
    onValue(gameReference, (snapshot) => {
      const data = snapshot.val();
      
      if (!data) {
        setMessage("Game not found");
        return;
      }
      
      if (data.status === 'waiting') {
        // Join the game
        update(ref(database, `games/${gameIdToJoin}`), {
          guest: {
            name: playerNames[2],
            color: playerColors[2]
          },
          status: 'active'
        });
        
        setPlayerNames({
          1: data.host.name,
          2: playerNames[2]
        });
        
        setPlayerColors({
          1: data.host.color,
          2: playerColors[2]
        });
        
        setOpponentConnected(true);
        setIsConnected(true);
        setGameState('game');
        
        // Set up game state listener
        setupGameStateListener(gameIdToJoin);
      } else if (data.status === 'active' && data.guest) {
        // Game already has a guest
        setMessage("Game already has another player");
      } else {
        setMessage("Game is not available");
      }
    }, {
      onlyOnce: true
    });
  };

  // Set up listener for game state changes
  const setupGameStateListener = (gameId) => {
    const gameStateRef = ref(database, `games/${gameId}`);
    gameRef.current = gameStateRef;
    
    onValue(gameStateRef, (snapshot) => {
      const data = snapshot.val();
      
      if (!data) return;
      
      // Update local game state from Firebase
      if (data.edges) setEdges(Array.isArray(data.edges) ? data.edges : []);
      if (data.triangles) setTriangles(Array.isArray(data.triangles) ? data.triangles : []);
      if (data.scores) setScores(data.scores);
      if (data.remainingMoves) setRemainingMoves(data.remainingMoves);
      
      // Reset timer when turn changes
      if (data.currentPlayer && data.currentPlayer !== currentPlayer) {
        setMoveTimer(60); // Reset to 1 minute
      }
      
      if (data.currentPlayer) {
        setCurrentPlayer(data.currentPlayer);
      }
      
      // Handle game challenges
      if (data.challenge && data.challenge.status === 'pending' && data.challenge.from !== playerRole) {
        setShowChallenge(true);
        setChallengeFrom(data.challenge.from === 1 ? playerNames[1] : playerNames[2]);
      }
      
      // Handle challenge responses
      if (data.challenge && data.challenge.status === 'accepted') {
        // Start a new game
        actuallyResetGame();
        setShowChallenge(false);
      } else if (data.challenge && data.challenge.status === 'declined') {
        // Go back to main menu
        setMessage("Challenge declined. Returning to main menu...");
        setTimeout(() => {
          cleanupAndGoToStart();
        }, 2000);
        setShowChallenge(false);
      }
      
      // Update game status
      if (data.status === 'gameOver') {
        setGameOver(true);
        
        if (data.winReason === 'timeout') {
          const winner = data.winner;
          setMessage(`Time's up! ${playerNames[winner]} wins!`);
        } else if (data.winReason === 'abandoned') {
          // Check if this player is the winner or loser
          if (data.loser === playerRole) {
            setMessage(`You lost the game because you left.`);
          } else {
            setMessage(`Congratulations! You won because your opponent left the game.`);
          }
          // Redirect to main menu after a delay
          setTimeout(() => {
            cleanupAndGoToStart();
          }, 3000);
        } else {
          const winner = data.scores[1] > data.scores[2] ? 1 : data.scores[1] < data.scores[2] ? 2 : 'Tie';
          setMessage(winner === 'Tie' ? "Game over! It's a tie!" : `Game over! ${playerNames[winner]} wins!`);
        }
      }
    });
  };

  // Check for game ID in URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const gameIdFromUrl = urlParams.get('game');
    
    if (gameIdFromUrl) {
      console.log("Found game ID in URL:", gameIdFromUrl);
      setGameState('joinMultiplayer');
      setGameMode('onlineMultiplayer');
    }
    
    return () => {
      // Clean up Firebase listeners
      if (gameRef.current) {
        console.log("Cleaning up game ref");
        off(gameRef.current);
      }
      if (connectionRef.current) {
        console.log("Cleaning up connection ref");
        off(connectionRef.current);
      }
    };
  }, []);

  // Sync game state to Firebase after local changes
  useEffect(() => {
    if (gameMode === 'onlineMultiplayer' && gameId && isConnected) {
      console.log("Syncing game state to Firebase:", {
        currentPlayer,
        edges: edges.length,
        triangles: triangles.length
      });
      
      update(ref(database, `games/${gameId}`), {
        edges,
        triangles,
        scores,
        remainingMoves,
        currentPlayer,
        status: gameOver ? 'gameOver' : 'active'
      }).catch(error => {
        console.error("Error updating game state:", error);
      });
    }
  }, [edges, triangles, scores, remainingMoves, currentPlayer, gameOver, gameMode, gameId, isConnected]);

  // AI move effect
  useEffect(() => {
    if (gameMode === 'onePlayer' && currentPlayer === 2 && !gameOver && validPaths.length > 0) {
      setAiThinking(true);
      
      // Simulate AI thinking
      const timer = setTimeout(() => {
        const aiMove = makeAIMove(pillars, edges, validPaths, triangles);
        
        if (aiMove) {
          // Create path between start and end pillars
          const orderedPath = aiMove.path;
          
          // Check if any of these edges already exist
          const newEdges = [
            { start: orderedPath[0], end: orderedPath[1] },
            { start: orderedPath[1], end: orderedPath[2] },
            { start: orderedPath[2], end: orderedPath[3] }
          ];
          
          // Filter to only include edges that don't already exist
          const filteredNewEdges = newEdges.filter(newEdge => 
            !edges.some(edge => 
              (edge.start === newEdge.start && edge.end === newEdge.end) || 
              (edge.start === newEdge.end && edge.end === newEdge.start)
            )
          );
          
          // Valid move, add new edges
          const updatedEdges = [...edges, ...filteredNewEdges];
          setEdges(updatedEdges);
          
          // Check for completed triangles
          const newTriangles = findNewTriangles(pillars, updatedEdges, triangles);
          setTriangles([...triangles, ...newTriangles.map(t => ({ ...t, player: currentPlayer }))]);
          
          // Update scores and remaining moves
          const newScores = { ...scores };
          newScores[currentPlayer] += newTriangles.length;
          setScores(newScores);
          
          const newRemainingMoves = { ...remainingMoves };
          newRemainingMoves[currentPlayer] -= 1;
          setRemainingMoves(newRemainingMoves);
          
          // Check if game is over
          if (newRemainingMoves[1] === 0 && newRemainingMoves[2] === 0) {
            setGameOver(true);
            const winner = newScores[1] > newScores[2] ? 1 : newScores[1] < newScores[2] ? 2 : 'Tie';
            setMessage(winner === 'Tie' ? "Game over! It's a tie!" : `Game over! ${playerNames[winner]} wins!`);
          } else {
            // Switch to next player
            setCurrentPlayer(1);
            setMessage(`${playerNames[1]}'s turn`);
          }
        } else {
          // No valid moves for AI
          const newRemainingMoves = { ...remainingMoves };
          newRemainingMoves[2] = 0;
          setRemainingMoves(newRemainingMoves);
          
          if (newRemainingMoves[1] === 0) {
            setGameOver(true);
            const winner = scores[1] > scores[2] ? 1 : scores[1] < scores[2] ? 2 : 'Tie';
            setMessage(winner === 'Tie' ? "Game over! It's a tie!" : `Game over! ${playerNames[winner]} wins!`);
          } else {
            setCurrentPlayer(1);
            setMessage(`${playerNames[1]}'s turn`);
          }
        }
        
        setAiThinking(false);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [currentPlayer, gameMode, gameOver, edges, pillars, remainingMoves, scores, triangles, validPaths, playerNames]);

  // Handle pillar click
  const handlePillarClick = (pillarId) => {
    if (gameOver || aiThinking) return;
    
    // In multiplayer mode, only allow moves on your turn
    if (gameMode === 'onlineMultiplayer' && currentPlayer !== playerRole) {
      setMessage("It's not your turn");
      return;
    }
    
    // If this is the first selection
    if (selections.length === 0) {
      // Check if the pillar is valid for starting a path
      const hasValidPath = validPaths.some(path => path[0] === pillarId || path[3] === pillarId);
      
      if (!hasValidPath) {
        setMessage("This pillar doesn't have any valid paths");
        return;
      }
      
      setSelections([pillarId]);
      
      // Find valid ending pillars from both forward and reverse paths
      const validEndPillars = validPaths.flatMap(path => {
        if (path[0] === pillarId) return [path[3]];
        if (path[3] === pillarId) return [path[0]];
        return [];
      });
      
      setHighlightedPillars([...new Set(validEndPillars)]);
      setMessage(`Select ending pillar to complete your move`);
    } 
    // If this is the second selection (completing a move)
    else if (selections.length === 1) {
      const startPillarId = selections[0];
      
      // Find a valid path between start and end pillars in either direction
      const validPath = validPaths.find(path => 
        (path[0] === startPillarId && path[3] === pillarId) ||
        (path[3] === startPillarId && path[0] === pillarId)
      );
      
      if (!validPath) {
        setMessage("Invalid move. Try again.");
        setSelections([]);
        setHighlightedPillars([]);
        return;
      }
      
      // Determine the correct path order based on selection
      const orderedPath = validPath[0] === startPillarId ? 
        validPath : 
        [validPath[3], validPath[2], validPath[1], validPath[0]];
      
      // Create new edges for this path
      const newEdges = [
        { start: orderedPath[0], end: orderedPath[1] },
        { start: orderedPath[1], end: orderedPath[2] },
        { start: orderedPath[2], end: orderedPath[3] }
      ];
      
      // Filter out edges that already exist
      const uniqueNewEdges = newEdges.filter(newEdge => 
        !edges.some(edge => 
          (edge.start === newEdge.start && edge.end === newEdge.end) || 
          (edge.start === newEdge.end && edge.end === newEdge.start)
        )
      );
      
      if (uniqueNewEdges.length === 0) {
        setMessage("All these edges already exist. Try a different path.");
        setSelections([]);
        setHighlightedPillars([]);
        return;
      }
      
      // Add new edges to the board
      const updatedEdges = [...edges, ...uniqueNewEdges];
      setEdges(updatedEdges);
      
      // Check for new triangles
      const newTriangles = findNewTriangles(pillars, updatedEdges, triangles);
      
      // Update triangles and score if new triangles were formed
      if (newTriangles.length > 0) {
        const updatedTriangles = [...triangles, ...newTriangles.map(t => ({...t, player: currentPlayer}))];
        setTriangles(updatedTriangles);
        
        // Update score
        const newScore = scores[currentPlayer] + newTriangles.length;
        setScores({...scores, [currentPlayer]: newScore});
        
        setMessage(`${playerNames[currentPlayer]} formed ${newTriangles.length} new triangle${newTriangles.length > 1 ? 's' : ''}!`);
      } else {
        setMessage(`${playerNames[currentPlayer === 1 ? 2 : 1]}'s turn`);
      }
      
      // Update remaining moves
      const newRemainingMoves = {...remainingMoves, [currentPlayer]: remainingMoves[currentPlayer] - 1};
      setRemainingMoves(newRemainingMoves);
      
      // Check if game is over
      if (newRemainingMoves[1] === 0 && newRemainingMoves[2] === 0) {
        setGameOver(true);
        const winner = scores[1] > scores[2] ? 1 : scores[1] < scores[2] ? 2 : 'Tie';
        setMessage(winner === 'Tie' ? "Game over! It's a tie!" : `Game over! ${playerNames[winner]} wins!`);
        
        // Update Firebase if in multiplayer mode
        if (gameMode === 'onlineMultiplayer' && gameId) {
          update(ref(database, `games/${gameId}`), {
            status: 'gameOver',
            winner: winner === 'Tie' ? null : winner
          });
        }
      } else {
        // Switch to the other player
        const nextPlayer = currentPlayer === 1 ? 2 : 1;
        setCurrentPlayer(nextPlayer);
        
        // Reset timer for the next player
        setMoveTimer(60);
        
        // If next player is AI and game is not over, trigger AI move
        if (gameMode === 'onePlayer' && nextPlayer === 2 && !gameOver) {
          // AI logic will be handled in the useEffect
        }
      }
      
      // Reset selections and highlighted pillars
      setSelections([]);
      setHighlightedPillars([]);
    }
  };

  // Fix the findNewTriangles function
  const findNewTriangles = (pillars, currentEdges, existingTriangles = []) => {
    const newTriangles = [];
    
    // Check all possible combinations of three pillars
    for (let i = 0; i < pillars.length; i++) {
      const pi = pillars[i].id;
      
      for (let j = i + 1; j < pillars.length; j++) {
        const pj = pillars[j].id;
        
        for (let k = j + 1; k < pillars.length; k++) {
          const pk = pillars[k].id;
          
          const triangle = findTriangle(pi, pj, pk, currentEdges);
          
          if (triangle) {
            // Check if this triangle already exists
            const exists = existingTriangles.some(t => 
              t.pillars.includes(pi) && 
              t.pillars.includes(pj) && 
              t.pillars.includes(pk)
            );
            
            if (!exists) {
              // Calculate the center position for the chess piece
              const center = {
                x: (pillars[i].x + pillars[j].x + pillars[k].x) / 3,
                y: (pillars[i].y + pillars[j].y + pillars[k].y) / 3
              };
              
              newTriangles.push({
                pillars: [pi, pj, pk],
                center
              });
            }
          }
        }
      }
    }
    
    return newTriangles;
  };

  // Reset the game
  const resetGame = () => {
    if (gameState === 'game' && !gameOver) {
      showConfirmDialog(
        'resetGame', 
        'Starting a new game will forfeit the current game. Are you sure?'
      );
      return;
    }
    
    setPillars(generateBoard());
    setEdges([]);
    setTriangles([]);
    setSelections([]);
    setCurrentPlayer(1);
    setScores({ 1: 0, 2: 0 });
    setRemainingMoves({ 1: 20, 2: 20 });
    setGameOver(false);
    setMessage(`${playerNames[1]}'s turn`);
    setHighlightedPillars([]);
    
    // Reset timer
    resetAndStartTimer();
    
    // For multiplayer games, reset the Firebase state too
    if (gameMode === 'onlineMultiplayer' && gameId) {
      update(ref(database, `games/${gameId}`), {
        edges: [],
        triangles: [],
        scores: { 1: 0, 2: 0 },
        remainingMoves: { 1: 20, 2: 20 },
        currentPlayer: 1,
        status: 'active'
      });
    }
  };

  // Go back to start page
  const goToStart = () => {
    if (gameState === 'game' && !gameOver) {
      showConfirmDialog(
        'goToStart', 
        'Going back to the main menu will forfeit the game. Are you sure?'
      );
      return;
    }
    
    // Clean up Firebase connections if in multiplayer mode
    if (gameMode === 'onlineMultiplayer' && gameId) {
      // Remove game data or mark as abandoned
      update(ref(database, `games/${gameId}`), {
        status: 'abandoned'
      });
      
      if (gameRef.current) {
        off(gameRef.current);
      }
    }
    
    setGameState('start');
    setGameId('');
    setPlayerRole(1);
    setIsConnected(false);
    setWaitingForPlayer(false);
    setOpponentConnected(false);
    setGameOver(false);
  };

  // Start menu option handlers
  const handleOnePlayer = () => {
    setGameMode('onePlayer');
    setPlayerNames({
      1: 'Player',
      2: 'AI'
    });
    setGameState('options');
  };

  const handleTwoPlayer = () => {
    setGameMode('twoPlayer');
    setPlayerNames({
      1: 'Player 1',
      2: 'Player 2'
    });
    setGameState('options');
  };

  // New handler for multiplayer option
  const handleMultiplayer = () => {
    setGameMode('onlineMultiplayer');
    setPlayerNames({
      1: 'Host',
      2: 'Guest'
    });
    createMultiplayerGame();
  };

  const handleStartGame = () => {
    setGameState('game');
    resetGame();
  };

  const handleColorChange = (player, color) => {
    setPlayerColors({
      ...playerColors,
      [player]: color
    });
  };

  const handleNameChange = (player, name) => {
    setPlayerNames({
      ...playerNames,
      [player]: name || `Player ${player}`
    });
  };

  // Copy share link to clipboard
  const copyShareLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setMessage("Link copied to clipboard!");
    setTimeout(() => setMessage("Waiting for opponent to join..."), 2000);
  };

  // Timer effect - keep running during confirmation dialog
  useEffect(() => {
    if (moveTimer > 0 && gameState === 'game' && !gameOver) {
      timerRef.current = setTimeout(() => {
        setMoveTimer(prev => prev - 1);
      }, 1000);
    } else if (moveTimer === 0 && gameState === 'game' && !gameOver) {
      // Time's up!
      handleTimeUp();
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [moveTimer, gameState, gameOver]);

  // Handle timer expiration
  const handleTimeUp = () => {
    setTimerActive(false);
    
    // Determine the winner (the other player)
    const winner = currentPlayer === 1 ? 2 : 1;
    
    // Update game state
    setGameOver(true);
    setMessage(`Time's up! ${playerNames[winner]} wins!`);
    
    // If in multiplayer mode, update Firebase
    if (gameMode === 'onlineMultiplayer' && gameId) {
      update(ref(database, `games/${gameId}`), {
        status: 'gameOver',
        winner: winner,
        winReason: 'timeout'
      });
    }
  };

  // Reset and start the timer
  const resetAndStartTimer = () => {
    setMoveTimer(60);
    setTimerActive(true);
  };

  // Pause the timer
  const pauseTimer = () => {
    setTimerActive(false);
  };

  // Show confirmation dialog - don't pause timer
  const showConfirmDialog = (action, message) => {
    setConfirmationAction(action);
    setConfirmationMessage(message);
    setShowConfirmation(true);
  };

  // Handle confirmation result - fix action execution
  const handleConfirmation = (confirmed) => {
    setShowConfirmation(false);
    
    if (confirmed && confirmationAction) {
      // Execute the confirmed action
      if (confirmationAction === 'goToStart') {
        handleGameAbandonment();
      } else if (confirmationAction === 'resetGame') {
        if (gameMode === 'onlineMultiplayer') {
          // In multiplayer, send a challenge instead of immediately resetting
          handleGameAbandonment();
          sendRematchChallenge();
        } else {
          // In single player or local multiplayer, just reset
          actuallyResetGame();
        }
      }
    }
  };

  // Handle game abandonment - update to track which player left and redirect properly
  const handleGameAbandonment = () => {
    // The player who is leaving/abandoning the game loses
    const losingPlayer = playerRole;
    const winningPlayer = losingPlayer === 1 ? 2 : 1;
    
    // If in multiplayer mode, update Firebase
    if (gameMode === 'onlineMultiplayer' && gameId) {
      update(ref(database, `games/${gameId}`), {
        status: 'gameOver',
        winner: winningPlayer,
        loser: losingPlayer,
        winReason: 'abandoned'
      }).then(() => {
        // After updating Firebase, redirect to main menu
        setTimeout(() => {
          cleanupAndGoToStart();
        }, 3000); // Give time to see the message
      });
    }
  };

  // Render timer
  const renderTimer = () => {
    const minutes = Math.floor(moveTimer / 60);
    const seconds = moveTimer % 60;
    const formattedTime = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    
    return (
      <div className={`flex items-center ${moveTimer <= 10 ? 'text-red-600' : 'text-gray-700'}`}>
        <Clock size={20} className="mr-2" />
        <span className="font-mono text-xl font-bold">{formattedTime}</span>
      </div>
    );
  };

  // Render confirmation dialog
  const renderConfirmationDialog = () => {
    if (!showConfirmation) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
          <h3 className="text-xl font-bold mb-4">Confirm Action</h3>
          <p className="mb-6 text-gray-700">{confirmationMessage}</p>
          <div className="flex justify-end space-x-4">
            <button 
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              onClick={() => handleConfirmation(false)}
            >
              Cancel
            </button>
            <button 
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
              onClick={() => handleConfirmation(true)}
            >
              Yes, I'm Sure
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render game over screen
  const renderGameOverScreen = () => (
    <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center z-40">
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4 shadow-2xl">
        <h2 className="text-3xl font-bold mb-4 text-center">{message}</h2>
        
        <div className="flex flex-col space-y-4 mt-8">
          {gameMode === 'onlineMultiplayer' ? (
            <button 
              className="w-full py-3 px-6 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-medium hover:bg-blue-700 transition-colors"
              onClick={sendRematchChallenge}
            >
              Challenge to Rematch
            </button>
          ) : (
            <button 
              className="w-full py-3 px-6 bg-blue-600 text-white rounded-xl flex items-center justify-center text-lg font-medium hover:bg-blue-700 transition-colors"
              onClick={resetGame}
            >
              Play Again
            </button>
          )}
          
          <button 
            className="w-full py-3 px-6 bg-gray-200 text-gray-800 rounded-xl flex items-center justify-center text-lg font-medium hover:bg-gray-300 transition-colors"
            onClick={goToStart}
          >
            Main Menu
          </button>
        </div>
      </div>
    </div>
  );

  // Render Start Screen with new multiplayer option
  const renderStartScreen = () => (
    <>
      <Helmet>
        <title>Chain Triangle Game - Connect Dots, Form Triangles</title>
        <meta name="description" content="Play Chain Triangle Game online - Connect dots to form triangles in this strategic board game. Play against AI, friends locally, or online multiplayer." />
      </Helmet>
      <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-violet-900 text-white">
        <div className="w-full max-w-lg p-8 bg-white/10 backdrop-blur-lg rounded-3xl shadow-2xl text-center border border-white/20">
          <div className="mb-12">
            <h1 className="text-7xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400 mb-4">
              Chain Triangle
            </h1>
            <p className="text-xl text-gray-300">Connect the dots, claim your triangles!</p>
          </div>
          
          <div className="space-y-4">
            <button 
              className="group w-full py-5 px-8 bg-gradient-to-r from-blue-500/80 to-blue-700/80 rounded-2xl flex items-center justify-center text-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-blue-500/30"
              onClick={handleOnePlayer}
            >
              <User size={28} className="mr-3 group-hover:rotate-12 transition-transform" />
              Single Player
            </button>
            
            <button 
              className="group w-full py-5 px-8 bg-gradient-to-r from-purple-500/80 to-purple-700/80 rounded-2xl flex items-center justify-center text-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-purple-500/30"
              onClick={handleTwoPlayer}
            >
              <Users size={28} className="mr-3 group-hover:rotate-12 transition-transform" />
              Two Players (Local)
            </button>
            
            <button 
              className="group w-full py-5 px-8 bg-gradient-to-r from-green-500/80 to-green-700/80 rounded-2xl flex items-center justify-center text-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg hover:shadow-green-500/30"
              onClick={handleMultiplayer}
            >
              <Share2 size={28} className="mr-3 group-hover:rotate-12 transition-transform" />
              Play with a Friend (Online)
            </button>
          </div>
        </div>
      </div>
    </>
  );

  // Render Options Screen
  const renderOptionsScreen = () => (
    <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-b from-indigo-900 to-purple-900 text-white">
      <div className="w-full max-w-lg p-8 bg-white rounded-2xl shadow-2xl text-center">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-2">
            Game Options
          </h1>
          <p className="text-gray-500">Customize your game experience</p>
        </div>
        
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-700 mb-4 text-left">Player Settings</h2>
          
          {/* Player 1 Settings */}
          <div className="mb-6 p-4 bg-gray-100 rounded-xl">
            <div className="flex items-center justify-between mb-4">
              <label className="text-gray-700 font-medium">Player 1 Name:</label>
              <input 
                type="text" 
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
                value={playerNames[1]}
                onChange={(e) => handleNameChange(1, e.target.value)}
                placeholder="Enter name"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <label className="text-gray-700 font-medium">Player 1 Color:</label>
              <div className="flex space-x-2">
                {['#3b82f6', '#10b981', '#8b5cf6', '#f97316', '#ec4899'].map(color => (
                  <button 
                    key={color}
                    className={`w-8 h-8 rounded-full border-2 ${playerColors[1] === color ? 'border-gray-800' : 'border-transparent'}`}
                    style={{ backgroundColor: color }}
                    onClick={() => handleColorChange(1, color)}
                  />
                ))}
              </div>
            </div>
          </div>
          
          {/* Player 2 Settings - Only show if two player mode */}
          {gameMode === 'twoPlayer' && (
            <div className="p-4 bg-gray-100 rounded-xl">
              <div className="flex items-center justify-between mb-4">
                <label className="text-gray-700 font-medium">Player 2 Name:</label>
                <input 
                  type="text" 
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 text-gray-700"
                  value={playerNames[2]}
                  onChange={(e) => handleNameChange(2, e.target.value)}
                  placeholder="Enter name"
                />
              </div>
              
              <div className="flex items-center justify-between">
                <label className="text-gray-700 font-medium">Player 2 Color:</label>
                <div className="flex space-x-2">
                  {['#ef4444', '#f59e0b', '#06b6d4', '#6366f1', '#84cc16'].map(color => (
                    <button 
                      key={color}
                      className={`w-8 h-8 rounded-full border-2 ${playerColors[2] === color ? 'border-gray-800' : 'border-transparent'}`}
                      style={{ backgroundColor: color }}
                      onClick={() => handleColorChange(2, color)}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
        
        <div className="flex justify-between">
          <button 
            className="py-3 px-6 bg-gray-500 text-white rounded-xl flex items-center justify-center text-lg font-medium transition-transform duration-200 hover:bg-gray-600"
            onClick={goToStart}
          >
            <ChevronLeft size={20} className="mr-1" />
            Back
          </button>
          
          <button 
            className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-xl flex items-center justify-center text-lg font-medium transition-transform duration-200 hover:scale-105"
            onClick={handleStartGame}
          >
            <PlayCircle size={20} className="mr-1" />
            Start Game
          </button>
        </div>
      </div>
    </div>
  );

  // New screen for creating multiplayer game and showing QR code
  const renderCreateMultiplayerScreen = () => (
    <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-b from-indigo-900 to-purple-900 text-white">
      <div className="w-full max-w-lg p-8 bg-white rounded-2xl shadow-2xl text-center">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-2">
            Invite a Friend
          </h1>
          <p className="text-gray-500">Share this QR code or link to play together</p>
        </div>
        
        <div className="mb-8 flex flex-col items-center">
          <div className="bg-white p-4 rounded-xl mb-6">
            <QRCodeSVG value={shareUrl} size={200} />
          </div>
          
          <div className="flex items-center w-full mb-6">
            <input 
              type="text" 
              value={shareUrl} 
              readOnly
              className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg text-gray-700"
            />
            <button 
              onClick={copyShareLink}
              className="bg-blue-600 text-white px-4 py-3 rounded-r-lg hover:bg-blue-700 transition-colors"
            >
              <Link size={20} />
            </button>
          </div>
          
          <div className="text-center">
            <p className="text-lg font-medium text-gray-700 mb-2">Game ID: {gameId}</p>
            <p className="text-gray-500">{waitingForPlayer ? "Waiting for opponent to join..." : "Opponent connected!"}</p>
            
            {waitingForPlayer && (
              <div className="mt-4 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
              </div>
            )}
          </div>
        </div>
        
        <div className="flex justify-between">
          <button 
            className="py-3 px-6 bg-gray-500 text-white rounded-xl flex items-center justify-center text-lg font-medium transition-transform duration-200 hover:bg-gray-600"
            onClick={goToStart}
          >
            <ChevronLeft size={20} className="mr-1" />
            Cancel
          </button>
          
          {opponentConnected && (
            <button 
              className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-xl flex items-center justify-center text-lg font-medium transition-transform duration-200 hover:scale-105"
              onClick={() => setGameState('game')}
            >
              <PlayCircle size={20} className="mr-1" />
              Start Game
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // New screen for joining a multiplayer game
  const renderJoinMultiplayerScreen = () => (
    <div className="flex flex-col items-center justify-center w-full min-h-screen bg-gradient-to-b from-indigo-900 to-purple-900 text-white">
      <div className="w-full max-w-lg p-8 bg-white rounded-2xl shadow-2xl text-center">
        <div className="mb-6">
          <h1 className="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600 mb-2">
            Join Game
          </h1>
          <p className="text-gray-500">Enter your name to join the game</p>
        </div>
        
        <div className="mb-8">
          <div className="mb-6">
            <label className="block text-left text-gray-700 font-medium mb-2">Your Name:</label>
            <input 
              type="text" 
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-700"
              value={playerNames[2]}
              onChange={(e) => handleNameChange(2, e.target.value)}
              placeholder="Enter your name"
            />
          </div>
          
          <div className="mb-6">
            <label className="block text-left text-gray-700 font-medium mb-2">Choose Color:</label>
            <div className="flex justify-center space-x-3">
              {['#ef4444', '#f59e0b', '#06b6d4', '#6366f1', '#84cc16'].map(color => (
                <button 
                  key={color}
                  className={`w-10 h-10 rounded-full border-2 ${playerColors[2] === color ? 'border-gray-800' : 'border-transparent'}`}
                  style={{ backgroundColor: color }}
                  onClick={() => handleColorChange(2, color)}
                />
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex justify-between">
          <button 
            className="py-3 px-6 bg-gray-500 text-white rounded-xl flex items-center justify-center text-lg font-medium transition-transform duration-200 hover:bg-gray-600"
            onClick={goToStart}
          >
            <ChevronLeft size={20} className="mr-1" />
            Cancel
          </button>
          
          <button 
            className="py-3 px-6 bg-gradient-to-r from-green-500 to-green-700 text-white rounded-xl flex items-center justify-center text-lg font-medium transition-transform duration-200 hover:scale-105"
            onClick={() => joinMultiplayerGame()}
          >
            <PlayCircle size={20} className="mr-1" />
            Join Game
          </button>
        </div>
      </div>
    </div>
  );

  // Render Player Card
  const renderPlayerCard = (playerNum) => (
    <div 
      className={`flex-1 p-6 rounded-2xl shadow-lg transition-all duration-300 ${
        currentPlayer === playerNum ? 'scale-105 shadow-xl' : ''
      }`}
      style={{ 
        background: currentPlayer === playerNum ? 
          `linear-gradient(135deg, ${playerColors[playerNum]}dd, ${playerColors[playerNum]}99)` : 
          'white',
        color: currentPlayer === playerNum ? 'white' : 'black'
      }}
    >
      <h2 className="text-2xl font-bold mb-3">{playerNames[playerNum]}</h2>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="opacity-90">Score:</span>
          <span className="text-xl font-semibold">{scores[playerNum]}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="opacity-90">Moves left:</span>
          <span className="text-xl font-semibold">{remainingMoves[playerNum]}</span>
        </div>
      </div>
    </div>
  );

  // Render Game Message
  const renderGameMessage = () => (
    <div className="flex-1 bg-white rounded-xl shadow-md p-4 mx-4 text-center flex flex-col items-center justify-center">
      <div className="text-xl font-medium mb-2">{message}</div>
      {gameState === 'game' && !gameOver && renderTimer()}
    </div>
  );

  // Separate the actual navigation from the confirmation
  const cleanupAndGoToStart = () => {
    // Clean up Firebase connections if in multiplayer mode
    if (gameMode === 'onlineMultiplayer' && gameId) {
      // Remove game data or mark as abandoned
      update(ref(database, `games/${gameId}`), {
        status: 'abandoned'
      });
      
      if (gameRef.current) {
        off(gameRef.current);
      }
    }
    
    // Remove game ID from URL
    if (window.history.pushState) {
      const newUrl = window.location.protocol + "//" + window.location.host;
      window.history.pushState({path: newUrl}, '', newUrl);
    }
    
    setGameState('start');
    setGameId('');
    setPlayerRole(1);
    setIsConnected(false);
    setWaitingForPlayer(false);
    setOpponentConnected(false);
    setGameOver(false);
  };

  // Separate the actual reset from the confirmation
  const actuallyResetGame = () => {
    setPillars(generateBoard());
    setEdges([]);
    setTriangles([]);
    setSelections([]);
    setCurrentPlayer(1);
    setScores({ 1: 0, 2: 0 });
    setRemainingMoves({ 1: 20, 2: 20 });
    setGameOver(false);
    setMessage(`${playerNames[1]}'s turn`);
    setHighlightedPillars([]);
    
    // Reset timer
    setMoveTimer(60);
    
    // For multiplayer games, reset the Firebase state too
    if (gameMode === 'onlineMultiplayer' && gameId) {
      update(ref(database, `games/${gameId}`), {
        edges: [],
        triangles: [],
        scores: { 1: 0, 2: 0 },
        remainingMoves: { 1: 20, 2: 20 },
        currentPlayer: 1,
        status: 'active'
      });
    }
  };

  // Add a function to send a challenge to the opponent
  const sendRematchChallenge = () => {
    if (gameMode === 'onlineMultiplayer' && gameId) {
      update(ref(database, `games/${gameId}`), {
        challenge: {
          from: playerRole,
          status: 'pending',
          timestamp: Date.now()
        }
      });
      setMessage("Challenge sent! Waiting for opponent...");
    }
  };

  // Add a function to respond to a challenge
  const respondToChallenge = (accept) => {
    if (gameMode === 'onlineMultiplayer' && gameId) {
      update(ref(database, `games/${gameId}`), {
        challenge: {
          from: challengeFrom === playerNames[1] ? 1 : 2,
          status: accept ? 'accepted' : 'declined',
          timestamp: Date.now()
        }
      });
      
      setShowChallenge(false);
    }
  };

  // Render challenge dialog
  const renderChallengeDialog = () => {
    if (!showChallenge) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
          <h3 className="text-xl font-bold mb-4">Rematch Challenge</h3>
          <p className="mb-6 text-gray-700">{challengeFrom} is challenging you to a rematch. Do you accept?</p>
          <div className="flex justify-end space-x-4">
            <button 
              className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
              onClick={() => respondToChallenge(false)}
            >
              Decline
            </button>
            <button 
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              onClick={() => respondToChallenge(true)}
            >
              Accept
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Effect to update message when player changes or game state changes
  useEffect(() => {
    if (gameState === 'game' && !gameOver) {
      setMessage(`${playerNames[currentPlayer]}'s turn`);
    }
  }, [currentPlayer, gameState, gameOver, playerNames]);

  return (
    <div className="App">
      {gameState === 'start' && renderStartScreen()}
      {gameState === 'options' && renderOptionsScreen()}
      {gameState === 'createMultiplayer' && renderCreateMultiplayerScreen()}
      {gameState === 'joinMultiplayer' && renderJoinMultiplayerScreen()}
      {gameState === 'game' && (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-200 py-8">
          <div className="max-w-6xl mx-auto px-4">
            <div className="flex justify-between items-center mb-8">
              <button 
                className="bg-gray-700 text-white px-4 py-2 rounded-lg flex items-center hover:bg-gray-800 transition-colors"
                onClick={goToStart}
              >
                <ChevronLeft size={16} className="mr-1" />
                Main Menu
              </button>
              
              <h1 className="text-4xl font-bold text-center text-gray-800 tracking-wide">
                Chain Triangle Game
              </h1>
              
              <button 
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                onClick={resetGame}
              >
                New Game
              </button>
            </div>
            
            {/* Player Status Cards */}
            <div className="flex justify-between gap-6 mb-8">
              {renderPlayerCard(1)}
              {renderGameMessage()}
              {renderPlayerCard(2)}
            </div>

            {/* Game Board */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8 mx-auto" style={{ maxWidth: '800px' }}>
              <svg width="900" height="600" viewBox="-40 -20 600 400" className="mx-auto">
                {/* Draw edges as double lines (rubber band effect) */}
                {edges.map((edge, index) => {
                  const start = pillars.find(p => p.id === edge.start);
                  const end = pillars.find(p => p.id === edge.end);
                  if (!start || !end) return null;
                  
                  // Calculate the midpoint between start and end
                  const midX = (start.x + end.x) / 2 + 15;
                  const midY = (start.y + end.y) / 2 + 15;
                  
                  // Calculate perpendicular offset for the double lines
                  const dx = end.x - start.x;
                  const dy = end.y - start.y;
                  const length = Math.sqrt(dx * dx + dy * dy);
                  const offsetX = -dy / length * 3; // 3px offset
                  const offsetY = dx / length * 3;  // 3px offset
                  
                  return (
                    <g key={`edge-${index}`}>
                      {/* First line (top/left side) */}
                      <path
                        d={`M${start.x + 15 + offsetX},${start.y + 15 + offsetY} 
                           Q${midX + offsetX * 1.5},${midY + offsetY * 1.5} 
                           ${end.x + 15 + offsetX},${end.y + 15 + offsetY}`}
                        stroke="#666"
                        strokeWidth="2"
                        fill="none"
                      />
                      
                      {/* Second line (bottom/right side) */}
                      <path
                        d={`M${start.x + 15 - offsetX},${start.y + 15 - offsetY} 
                           Q${midX - offsetX * 1.5},${midY - offsetY * 1.5} 
                           ${end.x + 15 - offsetX},${end.y + 15 - offsetY}`}
                        stroke="#666"
                        strokeWidth="2"
                        fill="none"
                      />
                    </g>
                  );
                })}
                
                {/* Draw triangles */}
                {triangles.map((triangle, index) => {
                  const p1 = pillars.find(p => p.id === triangle.pillars[0]);
                  const p2 = pillars.find(p => p.id === triangle.pillars[1]);
                  const p3 = pillars.find(p => p.id === triangle.pillars[2]);
                  
                  if (!p1 || !p2 || !p3) return null;
                  
                  return (
                    <g key={`triangle-${index}`}>
                      <polygon
                        points={`${p1.x + 15},${p1.y + 15} ${p2.x + 15},${p2.y + 15} ${p3.x + 15},${p3.y + 15}`}
                        fill={triangle.player === 1 ? 'rgba(0, 0, 255, 0.2)' : 'rgba(255, 0, 0, 0.2)'}
                        stroke={triangle.player === 1 ? 'blue' : 'red'}
                        strokeWidth="1"
                      />
                      <circle
                        cx={triangle.center.x + 15}
                        cy={triangle.center.y + 15}
                        r="6"
                        fill={triangle.player === 1 ? 'blue' : 'red'}
                      />
                    </g>
                  );
                })}
                
                {/* Draw pillars */}
                {pillars.map(pillar => (
                  <g
                    key={`pillar-${pillar.id}`}
                    onClick={() => handlePillarClick(pillar.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <circle
                      cx={pillar.x + 15}
                      cy={pillar.y + 15}
                      r="10"
                      fill={
                        selections.includes(pillar.id) ? 'green' :
                        highlightedPillars.includes(pillar.id) ? 'yellow' : 'gray'
                      }
                      stroke="black"
                      strokeWidth="2"
                    />
                    <text
                      x={pillar.x + 15}
                      y={pillar.y + 20}
                      textAnchor="middle"
                      fontSize="10"
                      fill="white"
                    >
                      {pillar.id}
                    </text>
                  </g>
                ))}
              </svg>
            </div>

            {/* Game Rules */}
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">Game Rules</h2>
              <ul className="space-y-3 text-gray-600">
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Players take turns connecting 4 consecutive pillars in a straight line
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Each move adds new edges connecting these pillars
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  When a triangle is formed, the current player claims it
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  Each player has 20 moves
                </li>
                <li className="flex items-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                  The player with the most triangles at the end wins
                </li>
              </ul>
            </div>
          </div>
        </div>
      )}
      {renderConfirmationDialog()}
      {renderChallengeDialog()}
      {gameOver && gameState === 'game' && renderGameOverScreen()}
      <Analytics />
    </div>
  );
};

export default ChainTriangleGame;